import { useAuth } from "@clerk/clerk-react";
import { Link } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import {
  CheckoutState,
  SplashPage,
  SubscriptionContext,
  SubscriptionTab,
} from "contexts/Subscription";
import { useContext, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  AddOnItemName,
  SearchParams,
  SubscriptionPortalFlow,
  UpgradeDialogSource,
} from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import {
  MonthlyPlanOnly,
  PlanAction,
  YearlyPlanOnly,
} from "features/Home/UpgradeDialog/Plan/helpers";

import { fetcherAuth } from "./api";
import { getParams, trackEvent } from "./tracking";
import { makeDeepCopy } from "./updateLocalState";

export const useSubscription = () => {
  const { setAlert, setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { currentOrg } = useContext(OrganizationUserContext);
  const {
    subscription,
    plans,
    setSubscription,
    upgradeDialogSource,
    setUpgradeDialogSource,
    setOpenCheckoutDialog,
    setManageDialog,
    setPlan,
    setTab,
  } = useContext(SubscriptionContext);
  const { getToken } = useAuth();
  const [checkoutLoading, setCheckoutLoading] = useState<
    Plans | AddOnItemName | null
  >(null);
  const [cancelLoading, setCancelLoading] = useState<boolean>(false);
  const cannotUpgrade =
    (subscription?.isSubscribed &&
      (subscription?.subscription?.planName === Plans.VA_YEARLY ||
        subscription?.subscription?.planName === Plans.VA)) ||
    currentOrg?.overridePlanName;

  const handleCheckout = async (
    plan?: Plans | AddOnItemName,
    pageSource?: string,
    source?: string | null,
    splashPageSource: SplashPage | null = null,
  ) => {
    if (plan) setPlan(plan);
    if (plan && MonthlyPlanOnly?.includes(plan)) {
      setTab(SubscriptionTab.monthly);
    }
    const fromSubscribePage = location.pathname.includes(
      `/${routes.subscribe}`,
    );

    if (fromSubscribePage) {
      source = "Mobile App";
    }

    const state: CheckoutState = {
      plan,
      source,
      pageSource,
      coupon: getParams(SearchParams.COUPON),
      splashPageSource,
      fromSubscribePage,
    };
    if (upgradeDialogSource) {
      if (YearlyPlanOnly?.includes(upgradeDialogSource)) {
        setTab(SubscriptionTab.yearly);
      }
      setOpenCheckoutDialog(state);
    } else {
      navigate(`/${routes.subscriptions}`, {
        state,
      });
    }
  };

  const handleCheckoutAddOnDialog = (
    upgradeDialogSource: UpgradeDialogSource,
    addOnItem: AddOnItemName,
  ) => {
    setUpgradeDialogSource(upgradeDialogSource);
    const state: CheckoutState = {
      plan: addOnItem,
      source: upgradeDialogSource,
      pageSource: window.location.pathname,
      coupon: getParams("coupon"),
    };
    setOpenCheckoutDialog(state);
  };

  const handleManagePlan = async (
    plan: Plans | AddOnItemName,
    flow?: SubscriptionPortalFlow,
  ) => {
    try {
      if (flow === SubscriptionPortalFlow.subscription_cancel) {
        setCancelLoading(true);
      } else {
        setCheckoutLoading(plan);
      }
      let url = `/api/organization/${currentOrg?.id}/payments/subscription/manage`;
      if (plan === AddOnItemName.partnership_data) {
        url = `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.partnershipData?.id}/manage`;
      } else if (plan === AddOnItemName.custom_email) {
        url = `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.customEmailData?.id}/manage`;
      }
      const res = await fetcherAuth(
        getToken,
        url,
        "POST",
        {},
        {
          flow,
        },
      );
      window.open(res.session.url, "_self");
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setCheckoutLoading(null);
      setCancelLoading(false);
    }
  };

  const renewSubscription = async (plan: Plans | AddOnItemName) => {
    let url = `/api/organization/${currentOrg?.id}/payments/subscription/renew`;
    if (plan === AddOnItemName.partnership_data) {
      url = `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.partnershipData?.id}/renew`;
    } else if (plan === AddOnItemName.custom_email) {
      url = `/api/organization/${currentOrg?.id}/payments-add-ons/${subscription?.customEmailData?.id}/renew`;
    }
    try {
      const { subscription, addOnItem } = await fetcherAuth(
        getToken,
        url,
        "POST",
        {},
      );
      if (addOnItem) {
        setSubscription((prev) => {
          const copy = makeDeepCopy(prev);
          if (plan === AddOnItemName.partnership_data) {
            copy.partnershipData = addOnItem;
          } else if (plan === AddOnItemName.custom_email) {
            copy.customEmailData = addOnItem;
          }
          return copy;
        });
      } else if (subscription) {
        setSubscription(subscription);
      }
      setAlert("Your plan has been successfully renewed!", "success");
    } catch (error) {
      setAlert(
        <>
          Unable to renew subscription. Please{" "}
          <Link
            onClick={() => {
              window.location.reload();
            }}
            sx={{
              color: "white",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            refresh the page
          </Link>{" "}
          and try again. Contact hello@onbento.com for assistance if the problem
          persists.
        </>,
        "error",
      );
    } finally {
      setCheckoutLoading(null);
    }
  };

  const onPlanButtonClick = async (
    plan: Plans | AddOnItemName,
    action: PlanAction,
  ) => {
    setPlan(plan);
    trackEvent(`User Clicked On Plan`, { plan, action });
    setCheckoutLoading(plan);
    const checkoutActions = [
      PlanAction.resubscribe,
      PlanAction.retry,
      PlanAction.upgrade,
      PlanAction.downgrade,
    ];
    if (checkoutActions.includes(action)) {
      await handleCheckout(plan);
    } else if (action === PlanAction.renew) {
      await renewSubscription(plan);
    } else if (action === PlanAction.manage) {
      setManageDialog(true);
    }
    setCheckoutLoading(null);
  };

  const decreaseEmailCount = () => {
    setSubscription((s) => {
      if (s) {
        return {
          ...s,
          remainingMessages: s.remainingMessages - 1,
        };
      } else {
        return s;
      }
    });
  };

  const decreaseChatCount = () => {
    setSubscription((s) => {
      if (s) {
        return {
          ...s,
          remainingChatMessages: s.remainingChatMessages - 1,
        };
      } else {
        return s;
      }
    });
  };

  const getPrice = (currPlan: Plans) => {
    const currentPrice = plans && plans[currPlan]?.price;
    if (
      subscription?.bannerPromoCode?.discountPercent &&
      subscription?.bannerPromoCode?.applicablePlans?.includes(currPlan)
    ) {
      return (
        (Number(currentPrice) *
          (100 - subscription?.bannerPromoCode?.discountPercent)) /
        100
      );
    } else {
      return currentPrice;
    }
  };

  return {
    handleCheckout,
    handleCheckoutAddOnDialog,
    handleManagePlan,
    renewSubscription,
    checkoutLoading,
    cannotUpgrade,
    onPlanButtonClick,
    decreaseEmailCount,
    decreaseChatCount,
    cancelLoading,
    getPrice,
  };
};
